import { CdkScrollable } from '@angular/cdk/scrolling';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, NgZone, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { CollabDocCommitEvent } from '@common/collab/events/collab-doc-commit.event';
import { CollabDocDeleteEvent } from '@common/collab/events/collab-doc-delete.event';
import { CollabDocDiscardEvent } from '@common/collab/events/collab-doc-discard.event';
import { CollabDocMergeEvent } from '@common/collab/events/collab-doc-merge.event';
import { CollabDocRenameEvent } from '@common/collab/events/collab-doc-rename.event';
import { CollabDocRestoreEvent } from '@common/collab/events/collab-doc-restore.event';
import { CollabUserJSON } from '@common/collab/models/collab-user-json.model';
import { DocTimelineUpdate } from '@common/collab/types/doc-timeline-update.type';
import { MetaDataValues } from '@common/collab/types/meta-data-values.type';
import { FlareCommands } from '@common/flare/flare-commands';
import { FlareSchema } from '@common/flare/flare-schema';
import { AnnotationAttributes } from '@common/flare/types/annotation-attributes.type';
import { DocSizeError } from '@common/html/constants/doc-size-error.constant';
import { convertMathMlEquationToLatex, dispatchEditEquationEvent } from '@common/prosemirror/commands/equation.command';
import { ImageAction, insertImageUploadErrors, removeImagePlaceholders, updateImagePlaceholders } from '@common/prosemirror/commands/image.command';
import { InsertInlineNodeCommand } from '@common/prosemirror/commands/insert-inline-node.command';
import { SelectionStyle } from '@common/prosemirror/commands/selection-format.command';
import { addColumnAfter, addColumnBefore, addRowAfter, addRowBefore, deleteColumn, deleteRow, deleteTable } from '@common/prosemirror/commands/table.command';
import { findNode } from '@common/prosemirror/model/node-find';
import { GutterItem, GutterItemType, GutterPluginTargetDecorationType, GutterPluginTargetItem, GutterPluginTargetType } from '@common/prosemirror/plugins/gutter.plugin';
import { ImagePlaceholderAction, ImageUploadPluginKey } from '@common/prosemirror/plugins/image-upload.plugin';
import { TrackedChangesColorsMetaKey, TrackedChangesIgnoreMetaKey } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { selectionIsInNode } from '@common/prosemirror/state/selection';
import { selectNodeContent } from '@common/prosemirror/state/transaction';
import { TrackedChangeUser } from '@common/prosemirror/tracked-changes/tracked-change-user.type';
import { getTrackedChangeIdsNodeAttr } from '@common/prosemirror/tracked-changes/tracked-changes';
import { updateNodeAttrs } from '@common/prosemirror/transform/node';
import { ReviewFileUserStatus } from '@common/reviews/enums/review-file-user-status.enum';
import { CollabSession, CollabSessionOptions } from '@common/sockets/collab-session';
import { CollabSessionSaveResult } from '@common/sockets/enums/collab-session-save-result.enum';
import { CollabSessionSyncState } from '@common/sockets/enums/collab-session-sync-state.enum';
import { CollabSessionErrorEvent } from '@common/sockets/types/collab-session-error-event.type';
import { cache } from '@common/util/cache.operator';
import { setColorLightness } from '@common/util/colors';
import { resolvePath } from '@common/util/path';
import { CommitFileDialogResult } from '@portal-core/commits/components/commit-file-dialog/commit-file-dialog.component';
import { MaxAttachmentSizeBytes } from '@portal-core/data/common/constants/max-attachment-size.constant';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { ErrorDialogComponent } from '@portal-core/general/components/error-dialog/error-dialog.component';
import { MatMenuPosition } from '@portal-core/general/models/mat-menu-position.model';
import { FileSizeService } from '@portal-core/general/services/file-size.service';
import { FileService } from '@portal-core/general/services/file.service';
import { LicenseUserSeatType } from '@portal-core/license-users/enums/license-user-seat-type.enum';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { FlareFileTextEditorGutterComponent } from '@portal-core/project-files/components/flare-file-text-editor-gutter/flare-file-text-editor-gutter.component';
import { FlareFileTextEditorTagbarComponent } from '@portal-core/project-files/components/flare-file-text-editor-tagbar/flare-file-text-editor-tagbar.component';
import { FlareFileTextEditorToolbarComponent } from '@portal-core/project-files/components/flare-file-text-editor-toolbar/flare-file-text-editor-toolbar.component';
import { ProjectFilesAddSnippetDialogComponent, ProjectFilesAddSnippetDialogData, ProjectFilesAddSnippetDialogResult } from '@portal-core/project-files/components/project-files-add-snippet-dialog/project-files-add-snippet-dialog.component';
import { ProjectFilesEditSnippetDialogComponent, ProjectFilesEditSnippetDialogData } from '@portal-core/project-files/components/project-files-edit-snippet-dialog/project-files-edit-snippet-dialog.component';
import { BackgroundStylesBuilder, LoadingConditionStylesBuilder, ProjectConditionsStyleService } from '@portal-core/project-files/conditions/services/project-conditions-style.service';
import { MetaDataGroup } from '@portal-core/project-files/enums/meta-data-group.enum';
import { ProjectFileProxyType } from '@portal-core/project-files/enums/project-file-proxy-type.enum';
import { EditorEvent } from '@portal-core/project-files/events/editor-event.event';
import { AnnotationCommentChangeEvent } from '@portal-core/project-files/models/annotation-comment-change-event.model';
import { StylesPanelEvent } from '@portal-core/project-files/models/styles-panel-event.model';
import { MetaDataApiService } from '@portal-core/project-files/services/meta-data-api.service';
import { MetaDataService } from '@portal-core/project-files/services/meta-data.service';
import { TextEditorCssStylesService } from '@portal-core/project-files/services/text-editor-css-styles.service';
import { PreviewCssStyle } from '@portal-core/project-files/stylesheets/models/preview-css-style.model';
import { FlareTextEditorType } from '@portal-core/project-files/types/flare-text-editor-type.type';
import { ReviewEditSnippetDialogComponent, ReviewEditSnippetDialogData } from '@portal-core/reviews/components/review-edit-snippet-dialog/review-edit-snippet-dialog.component';
import { ReviewFilesService } from '@portal-core/reviews/review-files/services/review-files.service';
import { CollabFileTextEditorComponent } from '@portal-core/text-editor/components/collab-file-text-editor/collab-file-text-editor.component';
import { CutCopyPasteHelpDialogComponent, CutCopyPasteHelpDialogData } from '@portal-core/text-editor/components/cut-copy-paste-help-dialog/cut-copy-paste-help-dialog.component';
import { SoloFileTextEditorComponent } from '@portal-core/text-editor/components/solo-file-text-editor/solo-file-text-editor.component';
import { EditorContextMenuEvent } from '@portal-core/text-editor/components/text-editor/text-editor.component';
import { EditorImageExtensions } from '@portal-core/text-editor/constants/editor-image-extensions.constant';
import { InsertImageEvent } from '@portal-core/text-editor/models/insert-image.event';
import { InsertSnippetEvent } from '@portal-core/text-editor/models/insert-snippet-event';
import { TextEditorService } from '@portal-core/text-editor/services/text-editor.service';
import { CollabSessionEvent } from '@portal-core/text-editor/types/collab-session-event.type';
import { EditorChangeEvent } from '@portal-core/text-editor/types/editor-change-event.type';
import { EditorToolbarDropdownMenuClosedEvent } from '@portal-core/ui/editor/types/editor-toolbar-dropdown-menu-closed-event.type';
import { FAILED_MATHML_CONVERSION_ERROR, isEditableEquationNode, isEquationNode, LOSSY_MATHML_CONVERSION_WARNING, tryConvertMathMlToLatex } from '@portal-core/ui/equation/util/equation-node';
import { SnackBarErrorComponent } from '@portal-core/ui/snack-bar/components/snack-bar-error/snack-bar-error.component';
import { SnackBarWarningComponent } from '@portal-core/ui/snack-bar/components/snack-bar-warning/snack-bar-warning.component';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { BidiBridge } from '@portal-core/util/bidi-bridge';
import { InputObservable } from '@portal-core/util/input-observable.decorator';
import { debounce, DebouncedFunc } from 'lodash';
import { selectAll } from 'prosemirror-commands';
import { redo, undo } from 'prosemirror-history';
import { ProseMirrorNode } from 'prosemirror-model';
import { Command, EditorState, NodeSelection, Plugin, TextSelection } from 'prosemirror-state';
import { isInTable } from 'prosemirror-tables';
import { EditorView, NodeViewConstructor } from 'prosemirror-view';
import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, filter, first, forkJoin, map, Observable, of, skip, Subject, Subscription, switchMap, tap } from 'rxjs';

@Component({
  selector: 'mc-flare-text-editor',
  templateUrl: './flare-text-editor.component.html',
  styleUrls: ['./flare-text-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TextEditorCssStylesService, ProjectConditionsStyleService]
})
@AutoUnsubscribe()
export class FlareTextEditorComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(CollabFileTextEditorComponent, { static: false }) collabFileTextEditor: CollabFileTextEditorComponent;
  @ViewChild(SoloFileTextEditorComponent, { static: false }) soloFileTextEditor: SoloFileTextEditorComponent;
  @ViewChild(FlareFileTextEditorTagbarComponent, { static: true }) flareFileTextEditorTagbarComponent: FlareFileTextEditorTagbarComponent;
  @ViewChild(FlareFileTextEditorToolbarComponent, { static: false }) flareFileTextEditorToolbarComponent: FlareFileTextEditorToolbarComponent;
  @ViewChild(FlareFileTextEditorGutterComponent, { static: false }) flareFileTextEditorGutterComponent: FlareFileTextEditorGutterComponent;
  @ViewChild(MatMenuTrigger, { static: true }) editorMenuTrigger: MatMenuTrigger;
  @ViewChild('editorScrollContainer', { static: true }) editorScrollContainerRef: ElementRef<HTMLElement>;
  @ViewChild('editorScrollContainer', { static: true, read: CdkScrollable }) editorScrollable: CdkScrollable;
  @Input() branchName: string;
  /** Whether the collab session can receive document updates. For example, it can be paused when the component is inactive or hidden. */
  @Input() canReceiveUpdates: boolean = true;
  @Input() commitId: string;
  @Input() content?: string;
  @Input() docExceedsMaxSize?: boolean;
  @Input() docSize?: number;
  @Input() editorTagNames?: Record<string, string>;
  @Input() editorType?: FlareTextEditorType = 'solo';
  @Input() featureSetVersion: number;
  @Input() fileId: number;
  @Input() filePath: string;
  @Input() fileType: CollabFileType;
  @Input() getTimelineDocVersion?: CollabSessionOptions['getTimelineDocVersion'];
  @Input() gutterEnabled?: boolean = true;
  @Input() generateEditorStyles?: boolean = false;
  @Input() generatePreviewStyles?: boolean = false;
  @Input() licenseUser: LicenseUser;
  @Input() parentComputedStylesheetClassNames?: string[] = [];
  @Input() parentFilePaths?: string[] = [];
  @Input() projectId: number;
  @Input() projectLanguage: string;
  @Input() readonly?: boolean = false;
  @Input() reviewPackageId?: number;
  @Input() showAI?: boolean = true;
  @Input() showStatusBar?: boolean = true
  @Input() showTagbar?: boolean = true
  @Input() showTrackedChanges?: boolean = true;
  @Input() timelineEnabled?: boolean = false;
  @Input() trackedChanges?: boolean = false;
  @Input() userDataEnabled?: boolean = true;
  @Input() usingWorkspaceTree?: boolean = false;

  /** Whether conditions are shown in the editor. */
  @Input() set showConditions(value: boolean | undefined) {
    if (this._showConditions !== value) {
      // Set the value
      this._showConditions = value;
      this.updateAppliedConditions();

      // Notify the node views
      this.nodeViewBridge.sendDown({
        type: 'showConditions-changed',
        data: {
          showConditions: value
        }
      });

      // Emit the change
      this.showConditionsChange.emit(this.showConditions);
    }
  }
  get showConditions(): boolean | undefined {
    return this._showConditions;
  }
  private _showConditions: boolean | undefined;

  /** Whether the styles panel is shown in the editor. */
  @Input() set showStylesPanel(value: boolean) {
    if (this._showStylesPanel !== value) {
      if (this.editorView && this.editorState) {
        this.flareCommands.setShowStylesPanel(this.editorState, this.editorView.dispatch, this.editorView, value);
      }

      this._showStylesPanel = value;
      this.showStylesPanelChange.emit(this.showStylesPanel);
    }
  }
  get showStylesPanel(): boolean {
    return this._showStylesPanel;
  }
  private _showStylesPanel: boolean = false;

  /** Whether styles are enabled in the editor. */
  @Input() set stylesEnabled(value: boolean) {
    if (this._stylesEnabled !== value) {
      // Update the editor state
      if (this.editorView && this.editorState) {
        this.flareCommands.setShowStyles(this.editorState, this.editorView.dispatch, this.editorView, value);
      }

      // Set the value
      this._stylesEnabled = value;

      // Notify the node views
      this.nodeViewBridge.sendDown({
        type: 'stylesEnabled-changed',
        data: {
          stylesEnabled: value,
          computedStylesheetClassNames: this.getParentComputedStylesheetClassNames()
        }
      });

      // Emit the change
      this.stylesEnabledChange.emit(this.stylesEnabled);
    }
  }
  get stylesEnabled(): boolean {
    return this._stylesEnabled;
  }
  private _stylesEnabled: boolean = true;

  @InputObservable('branchName') branchName$: Observable<string>;
  @InputObservable('commitId') commitId$: Observable<string>;
  @InputObservable('fileId') fileId$: Observable<number>;
  @InputObservable('filePath') filePath$: Observable<string>;
  @InputObservable('fileType') fileType$: Observable<CollabFileType>;
  @InputObservable('licenseUser') licenseUser$: Observable<LicenseUser>;
  @InputObservable('projectId') projectId$: Observable<number>;
  @InputObservable('content') content$: Observable<string>;
  @InputObservable('readonly') readonly$: Observable<boolean>;

  @Output() contentChange: EventEmitter<EditorChangeEvent> = new EventEmitter<EditorChangeEvent>();
  @Output() createCollabFile: EventEmitter<void> = new EventEmitter<void>();
  @Output() docCommit: EventEmitter<CollabDocCommitEvent> = new EventEmitter<CollabDocCommitEvent>();
  @Output() docDelete: EventEmitter<CollabDocDeleteEvent> = new EventEmitter<CollabDocDeleteEvent>();
  @Output() docDiscard: EventEmitter<CollabDocDiscardEvent> = new EventEmitter<CollabDocDiscardEvent>();
  @Output() docMerge: EventEmitter<CollabDocMergeEvent> = new EventEmitter<CollabDocMergeEvent>();
  @Output() docRename: EventEmitter<CollabDocRenameEvent> = new EventEmitter<CollabDocRenameEvent>();
  @Output() docRestore: EventEmitter<CollabDocRestoreEvent> = new EventEmitter<CollabDocRestoreEvent>();
  @Output() errorChange: EventEmitter<CollabSessionErrorEvent> = new EventEmitter<CollabSessionErrorEvent>();
  @Output() exceedsMaxSizeChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() exceedsMongoDBMaxSizeChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() load: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() navToFile: EventEmitter<string> = new EventEmitter<string>();
  @Output() ready: EventEmitter<EditorChangeEvent> = new EventEmitter<EditorChangeEvent>();
  @Output() selectionChange: EventEmitter<EditorChangeEvent> = new EventEmitter<EditorChangeEvent>();
  @Output() showConditionsChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() showStylesPanelChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() stylesEnabledChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() stylesPanelEvent: EventEmitter<StylesPanelEvent> = new EventEmitter<StylesPanelEvent>();
  @Output() stylesheetLoad: EventEmitter<void> = new EventEmitter<void>();
  @Output() stylesheetLoaded: EventEmitter<Error | undefined> = new EventEmitter<Error | undefined>();
  @Output() subEditorChange: EventEmitter<ReviewFileUserStatus> = new EventEmitter<ReviewFileUserStatus>();
  @Output() syncStateChange: EventEmitter<CollabSessionSyncState> = new EventEmitter<CollabSessionSyncState>();
  @Output() timelineUpdate: EventEmitter<DocTimelineUpdate> = new EventEmitter<DocTimelineUpdate>();
  @Output() trackedChangesChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() unload: EventEmitter<void> = new EventEmitter<void>();
  @Output() usersUpdate: EventEmitter<CollabUserJSON[]> = new EventEmitter<CollabUserJSON[]>();
  @Output() blockChanges: EventEmitter<boolean> = new EventEmitter<boolean>();

  @HostBinding('class.mc-flare-text-editor-collab') get _collabClassName() { return this.editorType === 'collab'; }
  @HostBinding('class.mc-flare-text-editor-sme') get _smeClassName() { return this.licenseUser?.SeatType === LicenseUserSeatType.SME; }
  @HostBinding('class.mc-flare-text-editor-solo') get _soloClassName() { return this.editorType === 'solo'; }

  public get docId(): string {
    return this.collabFileTextEditor?.docId;
  }

  public get docVersion(): number {
    return this.collabFileTextEditor?.docVersion;
  }

  public get docCreatedAt(): ISO8601DateString {
    return this.collabFileTextEditor?.docCreatedAt;
  }

  private _docCommittedAt: ISO8601DateString
  public get docCommittedAt(): ISO8601DateString {
    return this._docCommittedAt;
  }

  private _docModifiedAt: ISO8601DateString;
  public get docModifiedAt(): ISO8601DateString {
    return this._docModifiedAt;
  }

  get editorState(): EditorState {
    return this.collabFileTextEditor?.editorState ?? this.soloFileTextEditor?.editorState ?? null;
  }

  get editorView(): EditorView {
    return this.collabFileTextEditor?.editorView ?? this.soloFileTextEditor?.editorView ?? null;
  }

  get viewPluginOverlay(): HTMLElement {
    return this.collabFileTextEditor?.viewPluginOverlay ?? this.soloFileTextEditor?.viewPluginOverlay ?? null;
  }

  get editable(): boolean {
    return !this.readonly && !this.serverReadonly;
  }

  set doc(doc: ProseMirrorNode) {
    if (this.soloFileTextEditor) {
      this.soloFileTextEditor.doc = doc;
    }
  }

  get cssStyles$(): Observable<PreviewCssStyle[]> {
    return this.textEditorCssStylesService.availableStyles$;
  }

  get selectionStyle$(): Observable<SelectionStyle> {
    return this.textEditorCssStylesService.selectionStyle$;
  }

  CollabFileType: typeof CollabFileType = CollabFileType;

  collabSession: CollabSession;
  collabSessionError: CollabSessionErrorEvent;
  debouncedReflowGutterLayout: DebouncedFunc<(force?: boolean) => void>;
  docExceedsMongoDBMaxSize: boolean;
  editorMenuPosition: MatMenuPosition = { x: '0px', y: '0px' };
  gutterItems$: Subject<GutterItem[]> = new Subject<GutterItem[]>();
  nodeViews$: Observable<Dictionary<NodeViewConstructor>>;
  offline: boolean;
  /**
   * Emits when the plugin overlay element is ready to use.
   * This is a BehaviorSubject because the editor may destroy and create its plugin during its lifecycle so this needs to re-emit each time a new plugin is created and subscribes.
   */
  private viewPluginOverlay$: BehaviorSubject<HTMLElement> = new BehaviorSubject<HTMLElement>(null);
  private computedStylesheetClassName: string;
  private computedStylesheetClassName$: Observable<string>;
  private metaDataSource$: Subject<MetaDataValues> = new BehaviorSubject(null);
  private metaData$: Observable<MetaDataValues> = this.metaDataSource$.asObservable();
  private metaDataSubscription: Subscription;
  private nodeViewBridge: BidiBridge<EditorEvent, EditorEvent> = new BidiBridge<EditorEvent, EditorEvent>();
  plugins$: Observable<Plugin[]>;
  private readonlySubscription: Subscription;
  private reloadDataSource: BehaviorSubject<void> = new BehaviorSubject<void>(null);
  private reload$: Observable<void> = this.reloadDataSource.asObservable();
  private showConditionsSource$: Subject<boolean> = new BehaviorSubject(null);
  private showConditions$: Observable<boolean> = this.showConditionsSource$.asObservable();
  schema: FlareSchema = new FlareSchema();
  private flareCommands: FlareCommands = new FlareCommands(this.schema);
  selectedGutterItemId: string;
  serverReadonly: boolean = false;
  showGutter: boolean = false;
  getEditorState: () => EditorState = () => this.editorState;
  contentSubscription: Subscription;
  private conditionsResetSubscription: Subscription;
  private commitIdChangeSubscription: Subscription;
  private cssStylesSubscription: Subscription;
  private selectionStyleSubscription: Subscription;

  constructor(
    private cdr: ChangeDetectorRef,
    private conditionsStyleService: ProjectConditionsStyleService,
    private errorService: ErrorService,
    private dialog: MatDialog,
    private fileSizeService: FileSizeService,
    private fileService: FileService,
    private metaDataApiService: MetaDataApiService,
    private metaDataService: MetaDataService,
    private ngZone: NgZone,
    private reviewFilesService: ReviewFilesService,
    private snackBar: MatSnackBar,
    private textEditorService: TextEditorService,
    private textEditorCssStylesService: TextEditorCssStylesService
  ) { }

  ngOnInit() {
    this.textEditorCssStylesService.generateEditorStyles = this.generateEditorStyles;
    this.textEditorCssStylesService.generatePreviewStyles = this.generatePreviewStyles;

    // Emit styles panel events for the parent to relay to the page gutter styles panel
    this.cssStylesSubscription = this.textEditorCssStylesService.availableStyles$.subscribe(styles => {
      this.stylesPanelEvent.emit({ type: 'cssStyles', styles });
    });
    this.selectionStyleSubscription = this.textEditorCssStylesService.selectionStyle$.subscribe(style => {
      this.stylesPanelEvent.emit({ type: 'selectionStyle', style });
    });

    // Create a debounced version of reflowGutterLayout so it does not get called many times on window resize and node view loads
    this.debouncedReflowGutterLayout = debounce(this.reflowGutterLayout.bind(this), 300, { leading: true });

    // Build the computed stylesheet class name from the file path. This is used by plugins$ and nodeViews$ so cache the result to be shared
    this.computedStylesheetClassName$ = this.filePath$.pipe(
      switchMap(filePath => this.textEditorService.buildComputedStylesheetClassNameAsync(filePath)),
      tap(computedStylesheetClassName => this.computedStylesheetClassName = computedStylesheetClassName),
      cache()
    );
    this.plugins$ = this.createPlugins$();
    this.nodeViews$ = this.createNodeViews$();

    this.metaDataSubscription = this.metaData$.subscribe((data) => {
      if (data) {
        const conditionTags = this.metaDataService.extractConditionTagsFromMetaData(data.metaData);
        this.conditionsStyleService.setConditions(conditionTags);
        this.updateAppliedConditions();
      } else {
        this.conditionsStyleService.setConditions(null);
      }
    });

    // Listen for node view events
    this.nodeViewBridge.up$.subscribe(event => {
      this.ngZone.run(() => this.onNodeViewEvent(event));
    });

    this.contentSubscription = this.content$.subscribe(() => this.scrollToTop());

    this.readonlySubscription = this.readonly$.pipe(
      distinctUntilChanged()
    ).subscribe(() => {
      this.sendEditableChanged();
    });

    this.buildConditionalStyles();

    this.conditionsResetSubscription = combineLatest([
      this.projectId$,
      this.branchName$,
      this.commitId$
    ]).subscribe(() => {
      this.conditionsStyleService.reset();
    });

    this.commitIdChangeSubscription = this.commitId$.pipe(
      filter(commitId => !!commitId),
      distinctUntilChanged(),
      skip(1) // Skip the initial value to react only to changes
    ).subscribe(commitId => {
      // Notify the node views
      this.nodeViewBridge.sendDown({
        type: 'commitId-changed',
        data: {
          commitId
        }
      });

      // Reload styles on a new commit
      this.textEditorCssStylesService.reloadStyles();

      // Load the meta data for the new commit
      this.loadMetaData();
    });
  }

  ngAfterViewInit() {
    // The view plugin overlay is not available until after the view has been initialized so emit it here
    this.viewPluginOverlay$.next(this.viewPluginOverlay);
  }

  ngOnDestroy() {
    this.nodeViewBridge.complete();

    if (this.debouncedReflowGutterLayout) {
      this.debouncedReflowGutterLayout.cancel();
      this.debouncedReflowGutterLayout = null;
    }
  }

  onCollabSessionEnd(event: CollabSessionEvent) {
    this.collabSession = null;
  }

  onCollabSessionStart(event: CollabSessionEvent) {
    this.collabSession = event.session;

    this.flareFileTextEditorToolbarComponent?.updateState(null);
    this.flareFileTextEditorTagbarComponent?.updateState(null);
    this.textEditorCssStylesService.updateState(this.editorState);
  }

  onCollabSessionLoaded(success: boolean) {
    if (success) {
      this.loadMetaData();
      this.updateAppliedConditions();
      this.textEditorCssStylesService.load(this.projectId, this.fileType === CollabFileType.Workspace ? this.branchName : this.commitId, this.fileType, this.filePath, this.editorState.doc);
    } else {
      // The doc failed to load so the stylesheet cannot be loaded. Emit that its done loading with an error
      this.stylesheetLoaded.emit(new Error('Failed to load collab doc.'));
    }
    this.load.emit(success);
  }

  private onNodeViewEvent(event: EditorEvent) {
    // Listen for the size of the content changing to reflow the gutter
    if (event.type === 'node-view-size-changed' && event.data.nodeName === 'html') {
      this.debouncedReflowGutterLayout();
    }
  }

  onSoloEditorLoaded(event: EditorChangeEvent) {
    this.flareFileTextEditorToolbarComponent?.updateState(event.newEditorState);
    this.flareFileTextEditorTagbarComponent?.updateState(event.newEditorState);
    this.dispatchMetaDataLoad(event.editorView, event.newEditorState);
    this.updateAppliedConditions();
    this.textEditorCssStylesService.load(this.projectId, this.fileType === CollabFileType.Workspace ? this.branchName : this.commitId, this.fileType, this.filePath, event.newEditorState.doc);
    this.load.emit(true);
  }

  onDispatch(command: Command) {
    // Return focus to the editor before running a command from the toolbar
    this.editorView.focus();
    command(this.editorState, this.editorView.dispatch, this.editorView);
  }

  onTrackedChangesChanged($event: boolean) {
    this.trackedChanges = $event;
    this.trackedChangesChange.emit($event);
  }

  onToolbarDropdownMenuClosed(event: EditorToolbarDropdownMenuClosedEvent<Command>) {
    // Only give focus to the editor if an item wasn't clicked and the menu wasn't closed because the user tabbed away
    if (!event.item && event.source !== 'tab') {
      // A toolbar dropdown was closed so give focus back to the editor
      this.editorView.focus();
    }
  }

  onEditorContextMenu(editorEvent: EditorContextMenuEvent) {
    if (!this.readonly) {
      editorEvent.preventDefault();

      const editorState = this.editorState;
      const selection = editorState.selection as NodeSelection;
      const isInSnippet = selectionIsInNode(selection, editorState.schema.nodes.madcapsnippettext) || selectionIsInNode(selection, editorState.schema.nodes.madcapsnippetblock);
      let snippetSrc: string;
      if (isInSnippet) {
        snippetSrc = resolvePath(this.filePath, selection.node.attrs?.src);
      }

      let isInProxy = true;
      let proxyType: ProjectFileProxyType;
      let proxyCommand: InsertInlineNodeCommand;
      const nodes = this.schema.nodes;
      switch (selection.node?.type?.name) {
        case nodes.madcapbodyproxy.name:
          proxyType = ProjectFileProxyType.Body;
          proxyCommand = this.flareCommands.insertBodyProxy;
          break;
        case nodes.madcapbreadcrumbsproxy.name:
          proxyType = ProjectFileProxyType.Breadcrumbs;
          proxyCommand = this.flareCommands.insertBreadcrumbsProxy;
          break;
        case nodes.madcaptopictoolbarproxy.name:
          proxyType = ProjectFileProxyType.TopicToolbar;
          proxyCommand = this.flareCommands.insertTopicToolbarProxy;
          break;
        case nodes.madcapminitocproxy.name:
          proxyType = ProjectFileProxyType.MiniTOC;
          proxyCommand = this.flareCommands.insertMiniTOCProxy;
          break;
        case nodes.madcapglossaryproxy.name:
          proxyType = ProjectFileProxyType.Glossary;
          proxyCommand = this.flareCommands.insertGlossaryProxy;
          break;
        case nodes.madcapsearchbarproxy.name:
          proxyType = ProjectFileProxyType.SearchBar;
          proxyCommand = this.flareCommands.insertSearchBarProxy;
          break;
        case nodes.madcapmenuproxy.name:
          proxyType = ProjectFileProxyType.Menu;
          proxyCommand = this.flareCommands.insertMenuProxy;
          break;
        case nodes.madcapcentralaccountproxy.name:
          proxyType = ProjectFileProxyType.CentralAccount;
          proxyCommand = this.flareCommands.insertCentralAccountProxy;
          break;
        case nodes.madcapelearningtoolbarproxy.name:
          proxyType = ProjectFileProxyType.ELearningToolbar;
          proxyCommand = this.flareCommands.insertELearningToolbarProxy;
          break;
        default:
          isInProxy = false;
          break;
      }
      const openProxyDialog = () => this.flareFileTextEditorToolbarComponent.openProxyDialog(this.editorState, proxyType, proxyCommand);

      let reviewFileId: number;
      if (this.fileType === CollabFileType.Review) {
        reviewFileId = this.reviewFilesService.getItemByProperties('Path', snippetSrc, 'ReviewPackageId', this.reviewPackageId)?.Id;
      }

      const isInEquation = selection instanceof NodeSelection && isEquationNode(selection.node);
      const equationNodePos = isInEquation ? selection.from : null;
      const canEditEquation = isInEquation && isEditableEquationNode(selection.node);

      const menu = {
        undo: { disabled: !undo(editorState) },
        redo: { disabled: !redo(editorState) },
        delete: { disabled: !this.flareCommands.deleteSelection(editorState) },
        editTable: { disabled: !isInTable(editorState) },
        editSnippet: { disabled: !isInSnippet, snippetSrc: snippetSrc, reviewFileId: reviewFileId },
        editEquation: { disabled: !isInEquation, canEdit: canEditEquation, nodePos: equationNodePos },
        editProxy: { disabled: this.fileType === CollabFileType.Review || !isInProxy, editProxyCommand: openProxyDialog }
      };

      this.editorMenuPosition = {
        x: editorEvent.event.clientX + 'px',
        y: editorEvent.event.clientY + 'px'
      };

      this.editorMenuTrigger.menuData = { menu };
      this.editorMenuTrigger.openMenu();
    }

  }

  onOpenEditProxyDialog(editProxyCommand: () => boolean) {
    this.onDispatch(editProxyCommand);
  }

  onEditEquation(nodePos: number) {
    dispatchEditEquationEvent(this.editorView, nodePos);
  }

  onConvertEquationToLatex(nodePos: number) {
    const node = this.editorState.doc.nodeAt(nodePos);
    if (!node) {
      return;
    }

    const result = tryConvertMathMlToLatex(node);
    if (result.status === 'failed') {
      this.snackBar.openFromComponent(SnackBarErrorComponent, { data: FAILED_MATHML_CONVERSION_ERROR });
      return;
    }

    if (result.status === 'lossy') {
      this.snackBar.openFromComponent(SnackBarWarningComponent, { data: LOSSY_MATHML_CONVERSION_WARNING });
    }

    this.onDispatch(convertMathMlEquationToLatex(nodePos, result.latex));
  }

  onEditorStateChanged(event: EditorChangeEvent) {
    if (this.flareFileTextEditorToolbarComponent) {
      this.flareFileTextEditorToolbarComponent.updateState(event.newEditorState);
    }

    if (this.flareFileTextEditorTagbarComponent) {
      this.flareFileTextEditorTagbarComponent.updateState(event.newEditorState);
    }

    this.textEditorCssStylesService.updateState(this.editorState);
  }

  onErrorChanged(error: CollabSessionErrorEvent) {
    this.collabSessionError = error;
    this.errorChange.emit(error);
  }

  onExceedsMaxSizeChanged(exceedsMaxSize: boolean) {
    this.docExceedsMaxSize = exceedsMaxSize;
    this.exceedsMaxSizeChange.emit(exceedsMaxSize);
  }

  onExceedsMongoDBMaxSizeChanged(exceedsMongoDBMaxSize: boolean) {
    this.docExceedsMongoDBMaxSize = exceedsMongoDBMaxSize;
    this.exceedsMongoDBMaxSizeChange.emit(exceedsMongoDBMaxSize);
  }

  onOfflineChanged(offline: boolean) {
    this.offline = offline;
  }

  onReadOnlyChanged(serverReadonly: boolean) {
    this.serverReadonly = serverReadonly;
    this.sendEditableChanged();
  }

  onSizeChanged(size: number) {
    this.docSize = size;
  }

  onDocCommittedChanged(committedAt: ISO8601DateString) {
    this._docCommittedAt = committedAt;
  }

  onDocModifiedChanged(modifiedAt: ISO8601DateString) {
    this._docModifiedAt = modifiedAt;
  }

  onRevertDocToSavedState() {
    this.collabSession.revertDocToSavedState();
  }

  onSyncStateChanged(syncState: CollabSessionSyncState) {
    this.syncStateChange.emit(syncState);
  }

  onGutterItemHovered(gutterItem: GutterItem) {
    this.updateGutterDecorations(gutterItem, 'hover', [{ group: 'hover' }]);
  }

  onGutterItemSelected(gutterItem: GutterItem) {
    this.updateGutterDecorations(gutterItem, 'select', [{ group: 'select' }], true);
  }

  onInsertingAnnotation(attrs: AnnotationAttributes) {
    this.selectedGutterItemId = attrs['MadCap:guid'];
  }

  onAnnotationCommentChanged(event: AnnotationCommentChangeEvent) {
    const annotation = event.annotation;

    // Find the annotation node with the same guid
    const nodeInfo = findNode(this.editorView.state.doc, node => {
      return node.type.name === 'madcapannotation' && node.attrs['MadCap:guid'] === annotation['MadCap:guid'];
    });

    if (nodeInfo) {
      // Update the node that was found
      const tr = this.editorState.tr;

      updateNodeAttrs(tr, nodeInfo.node, nodeInfo.pos, {
        'MadCap:comment': annotation['MadCap:comment'],
        'MadCap:editor': this.licenseUser.User.FullName,
        'MadCap:editDate': new Date().toISOString()
      });

      // If the transaction changed the doc then dispatch it
      if (tr.docChanged) {
        // Do not track changes for annotations
        tr.setMeta(TrackedChangesIgnoreMetaKey, true);
        this.editorView.dispatch(tr);
      }
    }
  }

  onInsertColumnLeftClicked() {
    addColumnBefore(this.editorState, this.editorView.dispatch);
  }

  onInsertColumnRightClicked() {
    addColumnAfter(this.editorState, this.editorView.dispatch);
  }

  onInsertRowAboveClicked() {
    addRowBefore(this.editorState, this.editorView.dispatch);
  }

  onInsertRowBelowClicked() {
    addRowAfter(this.editorState, this.editorView.dispatch);
  }

  onDeleteColumnClicked() {
    deleteColumn(this.editorState, this.editorView.dispatch);
  }

  onDeleteRowClicked() {
    deleteRow(this.editorState, this.editorView.dispatch);
  }

  onDeleteTableClicked() {
    deleteTable(this.editorState, this.editorView.dispatch);
  }

  onInsertingImage(event: InsertImageEvent, showErrorDialog: boolean = false) {
    this.updateImages([event], showErrorDialog);
  }

  onOpenEditSnippetDialog(snippetSrc: string) {
    if (this.fileType === CollabFileType.Review) {
      this.dialog.open<ReviewEditSnippetDialogComponent, ReviewEditSnippetDialogData>(ReviewEditSnippetDialogComponent, {
        ...ReviewEditSnippetDialogComponent.DialogConfig,
        data: {
          changeCallback: (status: ReviewFileUserStatus) => this.subEditorChange.emit(status),
          featureSetVersion: this.featureSetVersion,
          navToFileCallback: (src: string) => this.navToFile.emit(src),
          onEditorSettingChanged: (name, value) => {
            if (name === 'showConditions') {
              this.showConditions = value;
            } else if (name === 'showStylesPanel') {
              this.showStylesPanel = value;
            } else if (name === 'stylesEnabled') {
              this.stylesEnabled = value;
            }
          },
          reviewer: this.licenseUser,
          reviewPackageId: this.reviewPackageId,
          showConditions: this.showConditions,
          snippetSrc: snippetSrc,
          stylesEnabled: this.stylesEnabled
        }
      });
    }
    else if (this.fileType === CollabFileType.Workspace) {
      this.dialog.open<ProjectFilesEditSnippetDialogComponent, ProjectFilesEditSnippetDialogData, CommitFileDialogResult>(ProjectFilesEditSnippetDialogComponent, {
        ...ProjectFilesEditSnippetDialogComponent.DialogConfig,
        data: {
          branchName: this.branchName,
          editorType: this.editorType,
          filePath: snippetSrc,
          licenseUser: this.licenseUser,
          navToFileCallback: (src: string) => this.navToFile.emit(src),
          onEditorSettingChanged: (name, value) => {
            if (name === 'showConditions') {
              this.showConditions = value;
            } else if (name === 'showStylesPanel') {
              this.showStylesPanel = value;
            } else if (name === 'stylesEnabled') {
              this.stylesEnabled = value;
            } else if (name === 'trackedChanges') {
              this.flareCommands.setTrackChanges(this.editorState, this.editorView.dispatch, this.editorView, value);
              this.trackedChanges = value;
              this.trackedChangesChange.emit(value);
            }
          },
          projectId: this.projectId,
          projectLanguage: this.projectLanguage,
          showConditions: this.showConditions,
          showStylesPanel: this.showStylesPanel,
          stylesEnabled: this.stylesEnabled,
          trackedChanges: this.trackedChanges
        }
      });
    }
  }

  onInsertProjectSnippetDialog(event: InsertSnippetEvent) {
    this.dialog.open<ProjectFilesAddSnippetDialogComponent, ProjectFilesAddSnippetDialogData, ProjectFilesAddSnippetDialogResult>(ProjectFilesAddSnippetDialogComponent, {
      ...ProjectFilesAddSnippetDialogComponent.DialogConfig,
      data: {
        commitId: this.commitId,
        projectId: this.projectId,
        branchName: this.branchName,
        filePath: this.filePath,
        licenseUser: this.licenseUser,
        projectLanguage: this.projectLanguage,
        schema: this.schema,
        usingWorkspaceTree: this.usingWorkspaceTree
      }
    }).afterClosed().subscribe((result: ProjectFilesAddSnippetDialogResult) => {
      if (result) {
        event.insertSnippetCommandCallback(result.path, result.isInlineContent);
      }
    });
  }

  onUndoClicked() {
    this.onDispatch(undo);
  }

  onRedoClicked() {
    this.onDispatch(redo);
  }

  onCutClicked() {
    if (document.queryCommandEnabled('cut')) {
      document.execCommand('cut');
    } else {
      this.openCutCopyPasteHelpDialog('cut');
    }
  }

  onCopyClicked() {
    if (document.queryCommandEnabled('copy')) {
      document.execCommand('copy');
    } else {
      this.openCutCopyPasteHelpDialog('copy');
    }
  }

  onPasteClicked() {
    if (document.queryCommandEnabled('paste')) {
      document.execCommand('paste');
    } else {
      this.openCutCopyPasteHelpDialog('paste');
    }
  }

  onDeleteClicked() {
    this.onDispatch(this.flareCommands.deleteSelection);
  }

  onSelectAllClicked() {
    this.onDispatch(selectAll);
  }

  onStyleSelected(styleName: string) {
    this.onDispatch((editorState: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView) => {
      const result = this.flareCommands.setFormat(editorState, dispatch, view, styleName);
      return result;
    });
  }

  onEditorContextMenuClosed() {
    // Return focus to the editor
    // Needs optional chaining since navigating to a file from the menu popup can destroy the editor view
    this.editorView?.focus();
  }

  public loadMetaData() {
    if (this.editorView && this.editorState) {
      this.dispatchMetaDataLoad(this.editorView, this.editorState);
    }
  }

  reflowGutterLayout(force: boolean = true) {
    if (force) {
      this.flareFileTextEditorGutterComponent?.forceLayout();
    } else {
      this.flareFileTextEditorGutterComponent?.layout();
    }
  }

  public reflowToolbarLayout() {
    this.flareFileTextEditorToolbarComponent?.checkViewport();
  }

  findImageUploadPos(editorState: EditorState, id: string | number): number {
    const decos = ImageUploadPluginKey.getState(editorState);
    const found = decos?.find(null, null, spec => spec.id === id);
    return found?.length > 0 ? found[0].from : null;
  }
  /*
   * Used to update the image placeholder with the uploaded image. If it fails, either show the error in a new error placeholder or the error dialog.
   * Use the error dialog if the image came from the insert image command.
   * Use the error placeholder if the image came from the clipboard.
  */
  updateImages(insertImageOptions: InsertImageEvent[], showErrorDialog: boolean = false) {
    const errorActions: ImagePlaceholderAction[] = [];

    // Send the images to the server
    const uploadImages$ = insertImageOptions.map(option => {
      const image = new FormData();
      image.append('reviewImage', option.file);
      return this.textEditorService.uploadImage$(this.fileId, image).pipe(
        map(res => {
          const action: ImageAction = {
            id: option.guid,
            pos: null,
            url: res.Url,
            attrs: option.attrs
          }
          return action;
        }),
        catchError(error => {
          errorActions.push({ id: option.guid, errors: this.errorService.getErrorMessages(error) })
          return of(null);
        })
      );
    });
    forkJoin(uploadImages$).pipe(
      // Filter out the empty values left by error handling
      map(actions => actions.filter(action => !!action))
    ).subscribe(actions => {
      // Insert the image nodes together to group them in one transaction
      updateImagePlaceholders(actions)(this.editorState, this.editorView.dispatch);

      if (errorActions.length) {
        // Either displays the error placeholder in the editor or shows the error dialog
        if (!showErrorDialog) {
          insertImageUploadErrors(errorActions)(this.editorState, this.editorView.dispatch);
        } else {
          removeImagePlaceholders(errorActions)(this.editorState, this.editorView.dispatch);

          this.dialog.open(ErrorDialogComponent, {
            ...ErrorDialogComponent.DialogConfig,
            data: {
              title: 'Error Uploading Image',
              message: 'An unexpected error happened while uploading an image',
              errors: errorActions[0].errors
            }
          });
        }
      }
    });
  }

  updateGutterDecorations(gutterItem: GutterItem, group: string, removes: Partial<GutterPluginTargetItem>[] = [], selectItemInDoc: boolean = false) {
    const adds: GutterPluginTargetItem[] = [];
    const tr = this.editorState.tr;

    // Find the gutter item's target in the doc in order to update the gutter plugin's decorations
    if (gutterItem) {
      // Iterate through the doc finding the targets of the gutter item
      this.editorState.doc.descendants((node, pos) => {
        if (gutterItem.type === GutterItemType.Annotation) {
          // If this node is the gutter item's target
          if (node.attrs['MadCap:guid'] === gutterItem.id) {
            adds.push({
              from: pos,
              group: group,
              id: gutterItem.id,
              targetType: GutterPluginTargetType.Annotation,
              to: pos + node.nodeSize,
              type: GutterPluginTargetDecorationType.Inline
            });

            if (selectItemInDoc) {
              selectNodeContent(tr, node, pos);
            }
          }
        } else if (gutterItem.type === GutterItemType.Change) {
          // If this node is the gutter item's target
          if (getTrackedChangeIdsNodeAttr(node.attrs) === gutterItem.changeIds) {
            adds.push({
              from: pos,
              group,
              id: gutterItem.id,
              targetType: GutterPluginTargetType.Change,
              to: pos + node.nodeSize,
              type: GutterPluginTargetDecorationType.Node
            });

            if (selectItemInDoc) {
              selectNodeContent(tr, node, pos);
            }
          }

          // Check the node's marks for this change
          if (Array.isArray(node.marks)) {
            node.marks.forEach(mark => {
              // If this mark is the gutter item's target
              if (mark.type.name === this.schema.madcapChangeMarkName && getTrackedChangeIdsNodeAttr(mark.attrs) === gutterItem.changeIds) {
                adds.push({
                  from: pos,
                  group,
                  id: gutterItem.id,
                  targetType: GutterPluginTargetType.Change,
                  to: pos + node.nodeSize,
                  type: GutterPluginTargetDecorationType.Inline
                });

                if (selectItemInDoc) {
                  tr.setSelection(TextSelection.create(tr.doc, pos, pos + node.nodeSize));
                }
              }
            });
          }
        }
      });
    }

    if (adds.length > 0 || removes.length > 0) {
      tr.setMeta('gutter', {
        adds,
        removes
      });

      this.editorView.dispatch(tr);
    }
  }

  markAsPristine() {
    this.collabFileTextEditor?.markAsPristine();
    this.soloFileTextEditor?.markAsPristine();
  }

  save(): Promise<CollabSessionSaveResult> {
    return this.collabSession.save();
  }

  reload() {
    this.reloadDataSource.next(null);
    this.collabFileTextEditor?.reload();
  }

  openCutCopyPasteHelpDialog(action: 'copy' | 'cut' | 'paste') {
    this.dialog.open<CutCopyPasteHelpDialogComponent, CutCopyPasteHelpDialogData>(CutCopyPasteHelpDialogComponent, {
      ...CutCopyPasteHelpDialogComponent.DialogConfig,
      data: {
        action
      }
    });
  }

  onCreateCollabFile() {
    this.createCollabFile.emit();
  }

  getContent(): string {
    return this.editorType === 'collab' ? this.collabFileTextEditor?.getContent() : this.soloFileTextEditor?.getContent();
  }

  updateStyles() {
    this.textEditorCssStylesService.load(this.projectId, this.fileType === CollabFileType.Workspace ? this.branchName : this.commitId, this.fileType, this.filePath, this.editorState.doc);
  }

  private createPlugins$(): Observable<Plugin[]> {
    // If the file info (branchName, filePath, fileType, fileId, or projectId) THEN the plugins need to be recreated
    // This is required because the text editor cannot reuse plugins from another file
    return combineLatest([
      this.projectId$, this.branchName$, this.filePath$, this.fileType$, this.computedStylesheetClassName$,
      this.reload$, this.readonly$, this.fileId$
    ]).pipe(
      tap(() => {
        // Reset meta data and show conditions before the plugins are created
        this.metaDataSource$.next(null);
        this.showConditionsSource$.next(null);
      }),
      map(([projectId, branchName, filePath, fileType, computedStylesheetClassName]) => {
        // If all the necessary properties exist then create a new set of plugins
        if (Number.isInteger(projectId) && filePath && fileType) {
          return this.textEditorService.createFlarePlugins(this.schema, {
            gutter: {
              onGutterItemsChanged: (items: GutterItem[], removed: GutterItem[], added: GutterItem[]) => {
                this.gutterItems$.next(items);
                this.showGutter = items?.length > 0;
                if (this.editorState?.tr) {
                  requestAnimationFrame(() => {
                    const tr = this.editorState.tr;
                    // just to trigger plugin
                    tr.setMeta(TrackedChangesColorsMetaKey, true);
                    this.editorView.dispatch(tr);
                  })
                }
                // Run detect changes to make the gutter update but wait until the gutter is showing and is its full width
                this.ngZone.onStable.asObservable().pipe(first()).subscribe(() => this.cdr.detectChanges());
              },
              schema: this.schema
            },
            pasteNormalizer: {
              onError: (error: Error) => {
                if (error.name === DocSizeError) {
                  this.snackBar.open(`Changes ignored because they exceed ${this.fileSizeService.format(this.textEditorService.flareTextEditorDocMaxCharLength, 0)} file size limit.`, 'OK');
                }
              }
            },
            trackedChanges: {
              enabled: this.trackedChanges,
              user: (): TrackedChangeUser => {
                if (this.licenseUser) {
                  return {
                    Id: this.licenseUser.User.Id,
                    Initials: this.licenseUser.User.Initials,
                    UserName: this.licenseUser.User.FullName
                  };
                } else {
                  return null;
                }
              }
            },
            keymaps: { 'Mod-k': () => this.ngZone.run(() => this.flareFileTextEditorToolbarComponent.openPropertiesLinkDialog(this.editorState)) },
            metaData: { metaData$: this.metaData$ },
            metaDataLoader: {
              onLoadMetaData$: () => {
                const hasValidReviewPackageId = this.fileType === CollabFileType.Review ? Number.isInteger(this.reviewPackageId) : true;
                // Make a request to get the meta data
                if (this.commitId && Number.isInteger(this.projectId) && this.fileType && hasValidReviewPackageId) {
                  return this.metaDataApiService.getMetaData$([MetaDataGroup.Variables, MetaDataGroup.Conditions, MetaDataGroup.Skins, MetaDataGroup.TOCs], {
                    commitId: this.commitId,
                    fileType: this.fileType,
                    projectId: this.projectId,
                    reviewPackageId: this.reviewPackageId
                  });
                } else {
                  return of(null);
                }
              },
              metaDataSource: this.metaDataSource$
            },
            selectionNormalizer: {
              selectParentForNodeNames: [
                'mcCentralContainer' // mc-central-container is hidden to the user so select its parent instead
              ]
            },
            view: { overlay$: this.viewPluginOverlay$, scrollable: this.editorScrollable },
            appliedConditions: {
              showConditions$: this.showConditions$,
              schema: this.schema,
              processAppliedConditions: (conditions) => {
                this.conditionsStyleService.processAppliedConditions(conditions);
              }
            },
            clipboardImageResolver: fileType === CollabFileType.Review ? {
              onPastedImages: insertImageOptions => {
                const errorActions: ImagePlaceholderAction[] = [];
                const unpackPromises$: Observable<InsertImageEvent>[] = insertImageOptions.map(option =>
                  // Check file validity
                  this.fileService.unpackImage$(option.file, MaxAttachmentSizeBytes, EditorImageExtensions).pipe(
                    map((fileInfo) => {
                      // If it ran successfully, use the original option
                      return fileInfo ? option : null;
                    }),
                    catchError(error => {
                      errorActions.push({ id: option.guid, errors: this.errorService.getErrorMessages(error) });
                      // Return non empty value to continue the join
                      return of(null);
                    })
                  )
                );
                forkJoin(unpackPromises$).pipe(
                  // Filter out the empty values left by error handling
                  map(insertImageOptions => insertImageOptions.filter(insertImageOptions => !!insertImageOptions))
                ).subscribe(insertImageOptions => {
                  if (errorActions.length) {
                    // Run the command to remove the upload placeholders
                    insertImageUploadErrors(errorActions)(this.editorState, this.editorView.dispatch);
                  }
                  // Upload images to server
                  this.updateImages(insertImageOptions);
                });
              }
            } : undefined,
            equationToolbarPopup: {
              isReadonly$: this.readonly$
            },
            nodeToolbarPopup: {
              onEdit: (src) => {
                const resolvedPath = resolvePath(this.filePath, src);
                this.onOpenEditSnippetDialog(resolvedPath);
              },
              onOpen: (src) => {
                const resolvedPath = resolvePath(this.filePath, src);
                this.navToFile.emit(resolvedPath);
              },
              isEditable: (src) => {
                if (this.fileType === CollabFileType.Workspace) {
                  return true;
                } else if (this.fileType === CollabFileType.Review) {
                  // Make sure that the file is included in the review package
                  const resolvedPath = resolvePath(this.filePath, src);
                  return !!this.reviewFilesService.getItemByProperties('Path', resolvedPath, 'ReviewPackageId', this.reviewPackageId);
                } else {
                  return false;
                }
              },
              isReadonly$: this.readonly$
            },
            computedStylesheet: {
              enabled: !!this.stylesEnabled,
              showPanel: !!this.showStylesPanel,
              getEditorClassName: (pluginState) => {
                return pluginState?.enabled ? this.getParentComputedStylesheetClassNames().join(' ') : 'mc-lwe-default-styles';
              },
              onEnabledChanged: (enabled) => {
                // Delay the event emission until after this plugin updates the editor attributes to add/remove the stylesheet class
                setTimeout(() => {
                  this.nodeViewBridge.sendDown({
                    type: 'stylesheet-toggled',
                    data: { projectId, branchName, filePath, className: computedStylesheetClassName, enabled }
                  });
                }, 0);
              },
              onStyleDecorationsBuilt: () => {
                // Delay the event emission until after this plugin updates the node styles
                setTimeout(() => {
                  this.nodeViewBridge.sendDown({
                    type: 'inline-styles-applied',
                    data: { projectId, branchName, filePath, className: computedStylesheetClassName }
                  });
                }, 0);
              },
              updateStyleLink: (link) => {
                return this.textEditorService.buildFlareContentApiUri(projectId, fileType === CollabFileType.Review ? this.commitId : branchName, filePath, link);
              },
              createView: () => {
                this.stylesheetLoad.emit();

                const loadSubscription = this.textEditorCssStylesService.stylesLoaded$.subscribe((error: any) => {
                  if (error) {
                    this.stylesheetLoaded.emit(new Error('Unable to generate stylesheet.', { cause: error }));
                  } else {
                    this.nodeViewBridge.sendDown({
                      type: 'stylesheet-loaded',
                      data: { projectId, branchName, filePath, className: computedStylesheetClassName }
                    });
                    this.stylesheetLoaded.emit();
                  }
                }, () => {
                  this.stylesheetLoaded.emit(new Error('Unable to generate stylesheet.'));
                });

                return {
                  destroy: () => {
                    loadSubscription?.unsubscribe();
                  }
                };
              }
            }
          });
        }
        // Else there are no plugins to create
        else {
          return null;
        }
      })
    );
  }

  private createNodeViews$(): Observable<Dictionary<NodeViewConstructor>> {
    return combineLatest([
      // If the file info changes THEN the node views need to be recreated
      this.filePath$, this.projectId$, this.branchName$, this.fileType$, this.computedStylesheetClassName$,
      // Or if there is a reload
      this.reload$
    ]).pipe(
      map(([filePath, projectId]) => {
        // If all the necessary properties exist then create a new set of node views
        if (filePath && Number.isInteger(projectId)) {
          return this.textEditorService.createFlareNodeViews(this.schema, this.nodeViewBridge, this.editorTagNames, {
            image: {
              buildContentApiUri: contentPath => this.textEditorService.buildFlareContentApiUri(this.projectId, this.commitId, this.filePath, contentPath),
              resolveCentralUrl: url => this.textEditorService.resolveCentralUrl(url)
            },
            madCapCodeSnippetBody: {
              editable: () => this.editable
            },
            madCapSnippet: {
              buildContentApiUri: contentPath => this.textEditorService.buildFlareContentApiUri(this.projectId, this.commitId, this.filePath, contentPath),
              branchName: () => this.branchName,
              commitId: () => this.commitId,
              filePath: () => this.filePath,
              fileType: () => this.fileType,
              licenseUser: () => this.licenseUser,
              parentComputedStylesheetClassNames: () => this.getParentComputedStylesheetClassNames(),
              parentFilePaths: () => this.parentFilePaths,
              projectId: () => this.projectId,
              projectLanguage: () => this.projectLanguage,
              resolveCentralUrl: url => this.textEditorService.resolveCentralUrl(url),
              reviewPackageId: () => this.reviewPackageId,
              showConditions: () => this.showConditions,
              stylesEnabled: () => this.stylesEnabled
            }
          });
        } else { // Else there is no node view config to create
          return null;
        }
      })
    );
  }

  private buildConditionalStyles(): void {
    const builders: BackgroundStylesBuilder[] = [];

    /* Add a style builder to create the background styles for the block elements: repeated inclined wide strips with fixed width and lightened colors */
    const blockDecoration = '.mc-madcap-condition-bg-decoration-block';
    const hostDecoration = '.mc-madcap-condition-bg-decoration-host';
    let builder = new BackgroundStylesBuilder({
      gradientAngle: 120,
      repeatable: true,
      conditionLineWidth: 20,
      definitionSuffix: '-block',
      patchSelector: (conditionSelector) => `mc-lwe-conditions-wrapper${conditionSelector}${blockDecoration}, ${hostDecoration}${conditionSelector}`,
      patchColor: (hexColor) => setColorLightness(hexColor, 90),
      unknownTagColor: '#777777' // TODO: grab the color from the theme (ThemeService)
    });
    builders.push(builder);

    /* Add a style builder to create the background styles for the inline elements: repeated inclined narrow strips with fixed width and lightened colors */
    const inlineDecoration = '.mc-madcap-condition-bg-decoration-inline:not(.mc-madcap-condition-decoration-tag-name-box)';
    const tagNameBoxDecoration = '.mc-madcap-condition-decoration-tag-name-box > *';
    builder = new BackgroundStylesBuilder({
      gradientAngle: 120,
      repeatable: true,
      conditionLineWidth: 10,
      definitionSuffix: '-inline',
      patchSelector: (conditionSelector) => `mc-lwe-conditions-wrapper${conditionSelector}${inlineDecoration}, mc-lwe-conditions-wrapper${conditionSelector}${tagNameBoxDecoration}, ${hostDecoration}${conditionSelector}`,
      patchColor: (hexColor) => setColorLightness(hexColor, 90),
      unknownTagColor: '#777777' // TODO: grab the color from the theme (ThemeService)
    });
    builders.push(builder);

    /* Add a style builder to create the background styles for the condition markers: not repeated vertical strips with original colors */
    const markerDecoration = '.mc-madcap-condition-bg-decoration-marker:not(.mc-madcap-condition-decoration-tag-name-box)';
    const markerTagNameBoxDecoration = '.mc-madcap-condition-bg-decoration-marker.mc-madcap-condition-decoration-tag-name-box > *';
    const loadingConditionCssSelector = `mc-lwe-conditions-wrapper${markerDecoration}:before, mc-lwe-conditions-wrapper${markerTagNameBoxDecoration}:before`;
    const loadingConditionAnimationName = 'topic-editor-condition-loading-animation';
    builder = new BackgroundStylesBuilder({
      gradientAngle: 90,
      definitionSuffix: '-marker',
      patchSelector: (selector) => `mc-lwe-conditions-wrapper${selector}${markerDecoration}:before, mc-lwe-conditions-wrapper${selector}${markerTagNameBoxDecoration}:before`,
      unknownTagColor: '#ffffff', // TODO: grab the color from the theme (ThemeService)
      loadingStylesBuilder: new LoadingConditionStylesBuilder(loadingConditionCssSelector, loadingConditionAnimationName)
    });
    builders.push(builder);

    this.conditionsStyleService.init(...builders);
  }

  public dispatchMetaDataLoad(editorView: EditorView, editorState: EditorState) {
    const tr = editorState.tr;
    tr.setMeta('metaDataLoader.load', true);
    editorView.dispatch(tr);
  }

  onShowConditionsToggle() {
    this.showConditions = !this.showConditions;
  }

  onShowStylesToggled() {
    this.stylesEnabled = !this.stylesEnabled;
  }

  onTrackedChangesToggle() {
    this.trackedChanges = !this.trackedChanges;
    this.trackedChangesChange.emit(this.trackedChanges);
  }

  scrollToTop() {
    this.editorScrollContainerRef?.nativeElement.scroll(0, 0);
  }

  private getParentComputedStylesheetClassNames(): string[] {
    return [
      ...(this.computedStylesheetClassName ? [this.computedStylesheetClassName] : []),
      ...(this.parentComputedStylesheetClassNames ?? [])
    ];
  }

  private sendEditableChanged() {
    this.nodeViewBridge.sendDown({
      type: 'editable-changed',
      data: {
        editable: this.editable
      }
    });
  }

  private updateAppliedConditions() {
    // repeat 'showConditions' value to force
    // 1) the appliedCondition plugin to update its state with conditions applied to the document
    // 2) the conditionsStyleService to rebuild styles
    this.showConditionsSource$.next(this.showConditions);
  }
}
